import { Component, Injector, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { FormlyFieldConfig, FormlyFormBuilder } from '@ngx-formly/core';
import { stepperEnum } from '../../common/enum/bulkExportEnum';
import { Art57Section } from '../../ui/Art57Section/Art57Section';
import { FieldDefinition } from '../../../sections/bulkUpdate/message/fieldDefinition';
import { SectionService } from '../../service/section-service/section-service';
import { BulkUtils } from '../../ui/BulkUtils';
import { BulkOperationSourceEnum } from '../../ui/Node/MedicinalProductsNode/BulkOperationSourceEnum';
import { BulkService } from '../../service/bulk-service/bulk.service';
import { BulkSection } from '../../ui/Art57Section/BulkSection';
import { filter, debounceTime, Subject } from 'rxjs';
import { ErrorControlUtil } from '../../ui/ErrorControlUtil';
import { ConfirmationService } from 'primeng/api';
import { BulkUpdateSection } from '../../ui/Art57Section/BulkUpdateSection';

@Component({
  selector: 'app-formly-bulk-update-view',
  templateUrl: './formly-bulk-update-view.component.html',
  styleUrl: './formly-bulk-update-view.component.scss',
})
export class FormlyBulkUpdateViewComponent {
  @ViewChild('stepper') stepper!: MatStepper;

  BulkOperationSourceEnum = BulkOperationSourceEnum;

  form = new UntypedFormGroup({});
  model: any;
  fieldDefinition: FieldDefinition;
  currentStepIndex: number = 0;

  title: {
    form: UntypedFormGroup | null;
    fields: FormlyFieldConfig[] | null;
  }[] = [];

  steps: {
    //TO-DO add type as enum
    type: string;
    label: string;
    tooltip: string;
    form: UntypedFormGroup | null;
    fields: FormlyFieldConfig[] | null;
    options: any;
  }[] = [];

  constructor(private injector: Injector,
              private confirmationService: ConfirmationService
  ) {
    this.fieldDefinition = new FieldDefinition(this.injector);
    this.injector
      .get(SectionService)
      .changeBulkUpdatedModel$.subscribe((source) => {
        if (source === BulkOperationSourceEnum.UPDATE) {
          this.updateFromSection();
        }
      });
  }

  private updateFromSection(): void {
    const section = this.injector
      .get(Art57Section)
      .getSelectedSection() as BulkSection;
    this.model = section.criteriaModel;
    this.currentStepIndex = section.selectedStep;
    // do NOT recreate `steps` or `title` here (avoid reassigning arrays that cause DOM re-creation)
  }

  ngOnInit() {
    this.updateFromSection();

    this.title = [
      {
        form: new UntypedFormGroup({}),
        fields: this.fieldDefinition.title,
      },
    ];

    this.steps = [
      {
        type: 'form',
        label: 'Filters',
        tooltip:
          'View the values / options of the filters. The filter is highlighted in blue and marked as selected if a value is chosen. ',
        form: new UntypedFormGroup({}),
        fields: this.fieldDefinition.filters,
        options: {},
      },
      {
        type: 'viewer',
        label: 'Requests',
        tooltip:
          'The list of bulk update requests made by the HQ organisation group',
        form: null,
        fields: null,
        options: {},
      },
      {
        type: 'action',
        label: 'Actions Set',
        tooltip:
          'The list of bulk update requests made by the HQ organisation group',
        form: new UntypedFormGroup({}),
        fields: this.fieldDefinition.actions,
        options: { fieldChanges: new Subject() },
      },
    ];

    const actionStep = this.steps[2];

    actionStep.options.fieldChanges
      ?.pipe(
        filter((e: any) => e.type === 'valueChanges'),
        debounceTime(50),
      )
      .subscribe(() => {
        const bulkUpdateSection = this.injector.get(Art57Section).bulkupdate as BulkUpdateSection;
        bulkUpdateSection.errors = ErrorControlUtil.findFieldErrors(
          actionStep.fields!,
          actionStep.form!,
          this.injector,
        );
      });
  }

  onStepChange(event: any) {
    this.currentStepIndex = event.selectedIndex;
    const section = this.injector
      .get(Art57Section)
      .getSelectedSection() as BulkSection;
    section.selectedStep = this.currentStepIndex;
    this.injector
      .get(BulkService)
      .notifyStepChange(this.currentStepIndex, BulkOperationSourceEnum.UPDATE);
  }

  getStepIcon(step: number) {
    let iconClass = '';

    switch (step) {
      case stepperEnum.firstStep:
        iconClass = 'bi-funnel';
        break;
      case stepperEnum.secondStep:
        iconClass = 'bi-file-text';
        break;
      case stepperEnum.thirdStep:
        iconClass = 'bi-file-text';
        break;
    }

    return iconClass;
  }

  getActiveFilters(): number {
    const activeWithData = BulkUtils.collectActiveFieldValue(this.model, [
      'filterStatus',
      'hasSelectedData',
    ]);
    const active = BulkUtils.collectActiveFieldValue(this.model, [
      'filterStatus',
    ]);
    return active == activeWithData ? activeWithData : -1;
  }

  getFiltersWithData(): number {
    const result = BulkUtils.collectActiveFieldValue(this.model, [
      'hasSelectedData',
    ]);
    return result;
  }

  getStepIConStatus(step: number) {
    let iconStatus = 'step-icon-gray';

    switch (step) {
      case stepperEnum.firstStep:
        const activeFilters = this.getActiveFilters();
        iconStatus =
          activeFilters == 0
            ? 'step-icon-gray'
            : activeFilters !== this.getFiltersWithData()
              ? 'step-icon-yellow'
              : 'step-icon-green';
        break;
      case stepperEnum.secondStep:
        iconStatus =
          !this.model.requestExecuting || this.model.requestExecuting == 0
            ? 'step-icon-gray'
            : 'step-icon-blink';
        break;
    }

    return iconStatus;
  }

  hasActionActive(): boolean {
    return (
      this.injector.get(Art57Section).isBulkUpdateSection() &&
      this.injector.get(Art57Section).bulkupdate.actionCache.actionEnabled
    );
  }

  filtersApplied() {
    return BulkUtils.getSelectedCheckboxLabels(
      this.steps[0].fields as FormlyFieldConfig[],
      this.model,
    );
  }
  showFiltesrsApplied(){
        this.confirmationService.confirm({
      header: 'Filters Applied',
      message: '<strong>The following filters has been applied</strong><br><span>' + this.filtersApplied() + '</span><br><br><span>',
      icon: 'pi pi-info-circle',
      rejectVisible: true,
      acceptVisible: false,     // oculta el Accept
      rejectLabel: 'Close',     // renombra el botón
      accept: () => {
        // Do nothing
      },
      reject: () => {
        // do nothing
      }
    });

  }

}
